import React, { useEffect, useRef, useState } from "react";

/**
 * CinematicPlate — full-bleed native HTML5 background video.
 *
 * Native MP4 = no embed restrictions, no YouTube branding, no controls,
 * no overlays, no player artifacts.
 *
 * Streamed from the FastAPI backend at /api/media/cinematic.mp4 with proper
 * HTTP byte-range support so the browser can progressively buffer/seek.
 * Original artifact (4K · 60fps · H.264 High@L5.2 · 588MB) failed in browsers
 * with MEDIA_ERR_SRC_NOT_SUPPORTED — this is the transcoded H.264 Main@L4
 * 1280×676 30fps ~16MB version.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const VIDEO_SRC = `${API_BASE}/api/media/cinematic.mp4`;

export default function CinematicPlate({
  caption = null,
  height = "h-[68vh] md:h-[78vh]",
  testid = "cinematic-plate",
}) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let cancelled = false;

    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    v.autoplay = true;
    v.preload = "auto";

    const onLoadedData = () => {
      if (cancelled) return;
      setReady(true);
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    const onCanPlay = () => {
      if (cancelled) return;
      setReady(true);
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("canplay", onCanPlay);

    // Imperatively (re)load. Survives StrictMode mount/unmount/remount cycle
    // where the previous request is aborted.
    try {
      v.load();
    } catch {}

    return () => {
      cancelled = true;
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  return (
    <section
      data-testid={testid}
      className={`relative w-full ${height} overflow-hidden bg-pearl`}
    >
      {/* Native cinematic video — fills the plate, no chrome, no clicks */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        style={{
          opacity: ready ? 1 : 0,
          transition: "opacity 1100ms ease",
        }}
      >
        {/* WebM/VP9 first — open codec, plays in every browser including
            codec-limited headless Chromium. MP4/H.264 second — universally
            supported by all real browsers (Chrome, Safari, Firefox, Edge). */}
        <source src={`${API_BASE}/api/media/cinematic.webm`} type="video/webm" />
        <source src={VIDEO_SRC} type="video/mp4" />
      </video>

      {/* Featherweight top + bottom ivory fades — keep cinematography intact */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(244,239,227,0.92) 0%, rgba(244,239,227,0) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(244,239,227,0.96) 0%, rgba(244,239,227,0) 100%)",
        }}
      />

      {/* Quiet manuscript caption */}
      {caption ? (
        <div className="absolute inset-x-0 bottom-0 z-10 pointer-events-none">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 pb-7 md:pb-9 flex items-baseline justify-between gap-4 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/85">
            <span>{caption.left}</span>
            {caption.center ? (
              <span className="hidden md:inline italic font-display text-[14px] text-tobacco/85 normal-case tracking-normal">
                {caption.center}
              </span>
            ) : null}
            <span>{caption.right}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
